import { idsIguais } from "../../utils/ids";
import React, { useRef } from "react";
import { Upload } from "lucide-react";
import * as XLSX from "xlsx/dist/xlsx.mini.min.js";
import Swal from "sweetalert2";

// Limites de importação — parse é client-side síncrono; blinda contra travar a aba.
const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB
const MAX_LINHAS = 2000;

function normalizeMatricula(value) {
  return String(value || "").trim();
}

// Reconhecimento na importação: nome completo OU login, ambos idênticos.
// Normaliza só trim + colapsa espaços internos + lowercase (case/spacing não
// invalidam; faltar parte do nome invalida — comparação é da string inteira).
function normalizeKey(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

export default function ImportarAlunos({
  usuarios = [],
  faculdadeId,
  idsJaExistentes = [],
  buttonLabel = "Importar lista",
  className = "",
  onImportConfirm,
}) {
  const fileInputRef = useRef(null);

  const handleOpenImport = async () => {
    if (!fileInputRef.current) return;

    const htmlTutorial = `
      <div style="text-align:left; color:#334e68;">
        <div style="border:1px solid #e9edf3; border-radius:12px; padding:14px; background:#f8fbfe; margin-top:8px;">
          <div style="font-size:15px; font-weight:700; margin-bottom:10px; color:#2e4a67;">
            Como importar a lista de alunos
          </div>

          <ol style="padding-left:18px; margin:0; line-height:1.7;">
            <li>Crie um arquivo <strong>Excel</strong> (.xlsx ou .xls).</li>
            <li>Preencha com o <strong>nome completo</strong> ou o <strong>login</strong> de cada aluno.</li>
            <li>Informe <strong>um por linha</strong> para evitar erros.</li>
            <li>O nome deve estar <strong>completo e idêntico</strong> ao cadastro; o login também precisa ser idêntico.</li>
            <li>Limite: até <strong>5 MB</strong> e <strong>2.000 registros</strong> por importação.</li>
            <li>Ao importar, o sistema vai mostrar quais alunos foram encontrados.</li>
          </ol>
        </div>
      </div>
    `;

    const result = await Swal.fire({
      icon: "info",
      title: "Tutorial de importação",
      html: htmlTutorial,
      width: 760,
      showCancelButton: true,
      confirmButtonText: "Continuar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#76a9da",
      cancelButtonColor: "#d9dee5",
      background: "#ffffff",
      color: "#334e68",
    });

    if (!result.isConfirmed) return;

    fileInputRef.current.value = "";
    fileInputRef.current.click();
  };

  const handleImportLista = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!faculdadeId) {
      await Swal.fire({
        icon: "warning",
        title: "Faculdade não identificada",
        text: "Não foi possível validar os alunos da importação.",
        confirmButtonColor: "#76a9da",
      });
      return;
    }

    if (file.size > MAX_FILE_BYTES) {
      await Swal.fire({
        icon: "warning",
        title: "Arquivo muito grande",
        text: `O arquivo tem ${(file.size / (1024 * 1024)).toFixed(1)} MB. O limite é de 5 MB. Divida a lista em partes menores.`,
        confirmButtonColor: "#76a9da",
      });
      return;
    }

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });

      const firstSheetName = workbook.SheetNames?.[0];
      if (!firstSheetName) {
        await Swal.fire({
          icon: "warning",
          title: "Arquivo vazio",
          text: "Não foi encontrada nenhuma aba no arquivo enviado.",
          confirmButtonColor: "#76a9da",
        });
        return;
      }

      const worksheet = workbook.Sheets[firstSheetName];

      const rows = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        raw: false,
        defval: "",
      });

      const itensLidos = rows
        .flat()
        .map((cell) => normalizeMatricula(cell))
        .filter(Boolean);

      if (!itensLidos.length) {
        await Swal.fire({
          icon: "warning",
          title: "Nenhum dado encontrado",
          text: "O arquivo não possui nomes ou logins válidos para importação.",
          confirmButtonColor: "#76a9da",
        });
        return;
      }

      if (itensLidos.length > MAX_LINHAS) {
        await Swal.fire({
          icon: "warning",
          title: "Arquivo muito grande",
          text: `O arquivo tem ${itensLidos.length} registros. O limite por importação é de ${MAX_LINHAS}. Divida a lista em partes menores.`,
          confirmButtonColor: "#76a9da",
        });
        return;
      }

      const itensUnicos = Array.from(new Set(itensLidos));

      const usuariosDaFaculdade = usuarios.filter((u) =>
        idsIguais(u.faculdadeId, faculdadeId),
      );

      // Reconhece por login (u.user) OU nome completo. Ambos normalizados igual.
      const mapaPorLogin = new Map(
        usuariosDaFaculdade.map((u) => [normalizeKey(u.user), u]),
      );
      const mapaPorNome = new Map(
        usuariosDaFaculdade.map((u) => [normalizeKey(u.nome), u]),
      );

      const encontrados = [];
      const naoEncontrados = [];

      itensUnicos.forEach((item) => {
        const chave = normalizeKey(item);
        const usuarioEncontrado =
          mapaPorLogin.get(chave) || mapaPorNome.get(chave) || null;

        if (usuarioEncontrado) {
          encontrados.push(usuarioEncontrado);
        } else {
          naoEncontrados.push(item);
        }
      });

      const encontradosUnicos = encontrados.filter(
        (item, index, arr) =>
          arr.findIndex((x) => idsIguais(x.id, item.id)) === index,
      );

      // IDs são GUID string — comparar como string (Number(guid) = NaN).
      const novosIds = encontradosUnicos.map((u) => u.id);
      const idsAtuais = new Set(idsJaExistentes.map(String));

      const realmenteNovos = novosIds.filter((id) => !idsAtuais.has(String(id)));
      const jaExistiam = novosIds.filter((id) => idsAtuais.has(String(id)));

      const htmlResumo = `
        <div style="text-align:left;">
          <div style="display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-top:8px;">
            <div style="border:1px solid #e9edf3; border-radius:10px; padding:12px; background:#f8fbfe;">
              <div style="font-size:12px; color:#667085;">Lidas no arquivo</div>
              <div style="font-size:20px; font-weight:700; color:#2e4a67;">${itensUnicos.length}</div>
            </div>

            <div style="border:1px solid #e9edf3; border-radius:10px; padding:12px; background:#f8fbfe;">
              <div style="font-size:12px; color:#667085;">Encontradas</div>
              <div style="font-size:20px; font-weight:700; color:#2e4a67;">${encontradosUnicos.length}</div>
            </div>

            <div style="border:1px solid #e9edf3; border-radius:10px; padding:12px; background:#f8fbfe;">
              <div style="font-size:12px; color:#667085;">Novos para adicionar</div>
              <div style="font-size:20px; font-weight:700; color:#2e4a67;">${realmenteNovos.length}</div>
            </div>

            <div style="border:1px solid #e9edf3; border-radius:10px; padding:12px; background:#f8fbfe;">
              <div style="font-size:12px; color:#667085;">Já estavam na lista</div>
              <div style="font-size:20px; font-weight:700; color:#2e4a67;">${jaExistiam.length}</div>
            </div>
          </div>
        </div>
      `;

      const result = await Swal.fire({
        icon: "info",
        title: "Conferir importação",
        html: htmlResumo,
        width: 760,
        showCancelButton: true,
        confirmButtonText: "Finalizar importação",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#76a9da",
        cancelButtonColor: "#d9dee5",
        background: "#ffffff",
        color: "#334e68",
      });

      if (!result.isConfirmed) return;

      if (!realmenteNovos.length) {
        await Swal.fire({
          icon: "info",
          title: "Nada para importar",
          text: "Todos os identificados já estavam vinculados.",
          confirmButtonColor: "#76a9da",
        });
        return;
      }

      onImportConfirm?.(realmenteNovos, {
        encontrados: encontradosUnicos,
        naoEncontrados,
        matriculasLidas: itensUnicos,
        jaExistiam,
      });

      await Swal.fire({
        icon: "success",
        title: "Importação concluída",
        text: `${realmenteNovos.length} aluno(s) foram adicionados com sucesso.`,
        confirmButtonColor: "#76a9da",
      });
    } catch (error) {
      console.error(error);

      await Swal.fire({
        icon: "error",
        title: "Erro ao importar",
        text: "Não foi possível ler o arquivo Excel.",
        confirmButtonColor: "#76a9da",
      });
    }
  };

  return (
    <>
      <button type="button" className={className} onClick={handleOpenImport}>
        <Upload size={14} />
        {buttonLabel}
      </button>

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: "none" }}
        onChange={handleImportLista}
      />
    </>
  );
}
